import React, { useState, useEffect } from 'react';
import { merchantService, Merchant } from '../../services/merchantService';

interface StoreSelectorProps {
  organizationId?: string;
  regionId?: string;
  value?: string;
  onChange: (storeId: string) => void;
  disabled?: boolean;
  required?: boolean;
  placeholder?: string;
  className?: string;
  excludeStoreId?: string; // To exclude current store when editing
}

const StoreSelector: React.FC<StoreSelectorProps> = ({
  organizationId,
  regionId,
  value,
  onChange,
  disabled = false,
  required = false,
  placeholder = 'Sélectionnez un magasin parent (optionnel)',
  className = '',
  excludeStoreId
}) => {
  const [stores, setStores] = useState<Merchant[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadStores = async () => {
      if (!organizationId) {
        setStores([]);
        setError(null);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        // Load merchants filtered by organization and optionally region
        const filters: any = {
          limit: 100, // Load more stores for selection
          is_active: true
        };

        // If we have filters in the API, we could filter by organization_id
        // For now, we'll load all active merchants and filter client-side
        const response = await merchantService.getAll(filters);
        
        // Filter stores by organization and region
        const filteredStores = response.merchants.filter((store: Merchant) => {
          // Exclude current store if specified
          if (excludeStoreId && store.merchant_id === excludeStoreId) {
            return false;
          }
          
          // Filter by organization
          if (store.organization_id !== organizationId) {
            return false;
          }
          
          // Filter by region if specified
          if (regionId && store.org_region_id !== regionId) {
            return false;
          }
          
          return true;
        });
        
        setStores(filteredStores);
      } catch (err) {
        console.error('Error loading stores:', err);
        setError('Impossible de charger les magasins');
        setStores([]);
      } finally {
        setLoading(false);
      }
    };

    loadStores();
  }, [organizationId, regionId, excludeStoreId]);

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChange(e.target.value);
  };

  if (!organizationId) {
    return (
      <div className={`relative ${className}`}>
        <select
          disabled
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 text-gray-500"
        >
          <option value="">Sélectionnez d'abord une marque globale</option>
        </select>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={`relative ${className}`}>
        <select
          disabled
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 text-gray-500"
        >
          <option value="">Chargement des magasins...</option>
        </select>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`relative ${className}`}>
        <select
          disabled
          className="w-full px-3 py-2 border border-red-300 rounded-md shadow-sm bg-red-50 text-red-500"
        >
          <option value="">{error}</option>
        </select>
        <p className="mt-1 text-sm text-red-600">{error}</p>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      <select
        value={value || ''}
        onChange={handleChange}
        disabled={disabled}
        required={required}
        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
      >
        <option value="">{placeholder}</option>
        {stores.map((store) => (
          <option key={store.merchant_id} value={store.merchant_id}>
            {store.business_name} 
            {store.store_code && ` (${store.store_code})`}
            {!store.is_active && ' (Inactive)'}
          </option>
        ))}
      </select>
      {stores.length === 0 && !loading && organizationId && (
        <p className="mt-1 text-sm text-gray-500">
          {regionId 
            ? 'Aucun magasin disponible dans cette région' 
            : 'Aucun magasin disponible pour cette marque globale'}
        </p>
      )}
    </div>
  );
};

export default StoreSelector;